// new.tsx

import { useTranslation } from "react-i18next";
import {
  adjustDataForSubmission,
  countries_options,
  preprocessCountryData,
} from "@/components/Form/CountryPhone/data";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import {
  AutocompleteField,
  AutocompleteLabelField,
  CheckboxField,
  InputField,
} from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { parseYes } from "@/features/FormCard/utils";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { Label } from "@radix-ui/react-label";
import { PortfoliosTechnicalInfoSection } from "@/components/PortfoliosTechnicalInfoSection";
import { fieldTranslations } from "./translations";

const FormSchema = z.object({
  client: z.string().transform((val) => val.toUpperCase()),
  vehicle: z.string().optional().nullable(),
  account_name: z.string(),
  short_account_name: z.string().nullable().optional(),
  name: z.string(),
  currency: z.string(),
  reporting_currency: z.string(),
  bank_name: z.string().optional().nullable(),
  account_type: z.string(),
  investment_type: z.string().nullable().optional(),
  contract_name: z.string().nullable().optional(),
  code: z.string(),
  has_pdf_bank_page: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),
  display_performance_report: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),
  bank_connection: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),
  bank_reporting_currency: z.preprocess(
    (val) => (val === "" ? null : val),
    z.string().nullable().optional(),
  ),
  collateral: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  strategy_id: z.coerce.number().optional().nullable(),

  bank_country: z.string().optional().nullable(),

  vehicle_index: z.coerce.number().int().nullable().optional(),
});

export function S3PortfoliosNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    options,
  } = useEditForm({
    client: Clients.S3,
    table_name: Tables.S3_PORTFOLIOS,
    FormSchema,
    isNew: true,
    adjustData: adjustDataForSubmission,
    adjustOnReset: preprocessCountryData,
  });

  const currencyOptions = useCurrencyOptions();

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("portfolios.new_portfolio")}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <Label>
                  {t("sections.client_info", {
                    defaultValue: "Informations client",
                  })}
                </Label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="client"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.client`, {
                      ns: "tables",
                      defaultValue: "Client",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="vehicle"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.vehicle`, {
                      ns: "tables",
                      defaultValue: "Véhicule / Bénéficiaire du compte",
                    })}
                    control={form.control}
                    isRequired
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteLabelField
                    name="reporting_currency"
                    label={t(
                      `${Tables.S3_PORTFOLIOS}.columns.reporting_currency`,
                      {
                        ns: "tables",
                        defaultValue: "Devise de reporting",
                      },
                    )}
                    isRequired
                    options={currencyOptions}
                    control={form.control}
                    className="min-w-0"
                  />
                </div>

                <Divider />
                <Label>
                  {t("sections.account_info", {
                    defaultValue: "Informations compte",
                  })}
                </Label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Compte",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="account_name"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.account_name`, {
                      ns: "tables",
                      defaultValue: "Groupe de comptes",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>

                {/* New */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="short_account_name"
                    label={t(
                      `${Tables.S3_PORTFOLIOS}.columns.short_account_name`,
                      {
                        ns: "tables",
                        defaultValue: "Nom court du compte",
                      },
                    )}
                    control={form.control}
                  />

                  <AutocompleteField
                    name="account_type"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.account_type`, {
                      ns: "tables",
                      defaultValue: "Type de compte",
                    })}
                    options={options?.["account_type"] ?? []}
                    isRequired
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteLabelField
                    name="currency"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.currency`, {
                      ns: "tables",
                      defaultValue: "Devise du portefeuille",
                    })}
                    options={currencyOptions}
                    isRequired
                    control={form.control}
                    className="min-w-0"
                  />
                  <InputField
                    name="code"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="bank_name"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.bank_name`, {
                      ns: "tables",
                      defaultValue: "Banque",
                    })}
                    isRequired
                    control={form.control}
                  />

                  <AutocompleteLabelField
                    name="bank_country"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.bank_country`, {
                      ns: "tables",
                      defaultValue: "Country",
                    })}
                    options={countries_options}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="investment_type"
                    label={t(
                      `${Tables.S3_PORTFOLIOS}.columns.investment_type`,
                      {
                        ns: "tables",
                        defaultValue: "Type d'investissement",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    name="contract_name"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.contract_name`, {
                      ns: "tables",
                      defaultValue: "Nom du contrat",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteLabelField
                    name="bank_reporting_currency"
                    label={t(
                      `${Tables.S3_PORTFOLIOS}.columns.bank_reporting_currency`,
                      {
                        ns: "tables",
                      },
                    )}
                    options={currencyOptions}
                    isRequired={false}
                    control={form.control}
                    className="min-w-0"
                  />
                  <CheckboxField
                    name="collateral"
                    label={t(`${Tables.S3_PORTFOLIOS}.columns.collateral`, {
                      ns: "tables",
                      defaultValue: "Compte-titres nanti",
                    })}
                    control={form.control}
                    className="flex h-full items-center"
                  />
                </div>
                <PortfoliosTechnicalInfoSection
                  form={form}
                  table_name={Tables.S3_PORTFOLIOS}
                  showReadOnlyFields={false}
                />
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
        >
          {changedFields
            ? NewDiff(
                changedFields,
                [],
                fieldTranslations,
                Tables.S3_PORTFOLIOS,
              )
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
